import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Eye, CheckCircle, X, Trophy, RotateCcw, BookOpen, AlertTriangle, Clock, FileText, Lock, PenLine } from 'lucide-react';
import api from '../../services/api';
import DocumentViewer from '../ui/DocumentViewer';
import SujetDocsButton from '../custom/SujetDocsButton';
import QrcPlayer from './QrcPlayer';
import { estimateSessionDuration } from '../../utils/format';

const NB_OPTIONS = [10, 20, 30];

// `scope` = slug de la thématique depuis laquelle on joue : il est transmis au
// player de réponses rédigées, qui en a besoin pour la politique d'accès (crédits).
function CardDetailPopup({ sousSujet, scope = null, onClose, onLaunch, onUnlock, accessible = true }) {
    const [showDocViewer, setShowDocViewer] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);
    const nbQ = sousSujet.nb_questions || 0;
    const answered = sousSujet.answered_count || 0;
    const mastered = sousSujet.mastered_count || 0;
    const answeredPct = nbQ > 0 ? Math.round((answered / nbQ) * 100) : 0;
    const masteredPct = nbQ > 0 ? Math.round((mastered / nbQ) * 100) : 0;
    const notSeen = nbQ - answered;
    const stats = sousSujet.stats;

    const [pickerNb, setPickerNb] = useState(Math.min(10, nbQ));
    const [pickerMode, setPickerMode] = useState('apprentissage');

    return (
        <div className="qcm-popup-overlay" onClick={onClose}>
            <div className="qcm-popup" onClick={(e) => e.stopPropagation()}>
                <button className="qcm-popup-close" onClick={onClose}><X size={18} /></button>

                <h3 className="qcm-popup-title">{sousSujet.label}</h3>
                {sousSujet.description && <p className="qcm-popup-desc">{sousSujet.description}</p>}
                {sousSujet.documents && sousSujet.documents.length > 0 ? (
                    <div className="qcm-popup-doc-fab" style={{ padding: 0, background: 'none', border: 'none' }}>
                        <SujetDocsButton documents={sousSujet.documents} compact />
                    </div>
                ) : sousSujet.doc_path && (
                    <>
                        <button className="qcm-popup-doc-fab" onClick={() => setShowDocViewer(true)}
                            title={sousSujet.doc_name || 'Voir le document source'}>
                            <FileText size={14} />
                        </button>
                        {showDocViewer && createPortal(
                            <DocumentViewer src={sousSujet.doc_path} name={sousSujet.doc_name} onClose={() => setShowDocViewer(false)} />,
                            document.body
                        )}
                    </>
                )}

                {/* Compact stats row */}
                <div className="qcm-popup-stats-compact">
                    <div className="qcm-popup-stat-compact">
                        <BookOpen size={15} style={{ color: 'var(--text-muted)' }} />
                        <strong>{nbQ}</strong> <span>questions</span>
                    </div>
                    {(sousSujet.qrc_count || 0) > 0 && (
                        <div className="qcm-popup-stat-compact">
                            <PenLine size={15} style={{ color: 'var(--primary)' }} />
                            <strong>{sousSujet.qrc_count}</strong> <span>rédigée{sousSujet.qrc_count > 1 ? 's' : ''}</span>
                        </div>
                    )}
                    {answered > 0 && (
                        <div className="qcm-popup-stat-compact">
                            <Eye size={15} style={{ color: 'var(--blue-400, #60a5fa)' }} />
                            <strong>{answered}</strong> <span>vues ({answeredPct}%)</span>
                        </div>
                    )}
                    {mastered > 0 && (
                        <div className="qcm-popup-stat-compact">
                            <CheckCircle size={15} style={{ color: 'var(--alert-success-color, #22c55e)' }} />
                            <strong>{mastered}</strong> <span>maîtrisées ({masteredPct}%)</span>
                        </div>
                    )}
                    {stats?.best_pct != null && (
                        <div className="qcm-popup-stat-compact">
                            <Trophy size={15} style={{ color: stats.best_pct >= 75 ? '#22c55e' : stats.best_pct >= 50 ? '#f59e0b' : '#ef4444' }} />
                            <strong>{stats.best_pct}%</strong> <span>meilleur</span>
                        </div>
                    )}
                    {stats?.attempts > 0 && (
                        <div className="qcm-popup-stat-compact">
                            <RotateCcw size={15} style={{ color: 'var(--text-muted)' }} />
                            <strong>{stats.attempts}</strong> <span>tentative{stats.attempts > 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>

                {/* Progression bars + legend — only if user has activity */}
                {answered > 0 && (
                    <div className="qcm-popup-progression">
                        <div className="qcm-popup-prog-row">
                            <Eye size={14} style={{ color: 'var(--blue-400, #60a5fa)' }} />
                            <div className="qcm-progression-bar" style={{ flex: 1 }}>
                                <div className="qcm-progression-fill qcm-progression-fill-seen" style={{ width: `${answeredPct}%` }} />
                            </div>
                            <span className="qcm-popup-prog-label">{answered}/{nbQ}</span>
                        </div>
                        <div className="qcm-popup-prog-row">
                            <CheckCircle size={14} style={{ color: 'var(--alert-success-color, #22c55e)' }} />
                            <div className="qcm-progression-bar" style={{ flex: 1 }}>
                                <div className="qcm-progression-fill qcm-progression-fill-ok" style={{ width: `${masteredPct}%` }} />
                            </div>
                            <span className="qcm-popup-prog-label">{mastered}/{nbQ}</span>
                        </div>
                    </div>
                )}

                {!!Number(sousSujet.has_session) && (
                    <div className="qcm-popup-session-info">
                        <span className="qcm-session-dot" />
                        Session en cours — mode {sousSujet.session_mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}
                    </div>
                )}

                <QrcPlayer sousSujetId={sousSujet.id} scope={scope} />

                {((sousSujet.documents && sousSujet.documents.length > 0) || sousSujet.doc_path) && onLaunch && nbQ > 0 && (
                    <div className="qcm-popup-doc-notice" onClick={() => {
                        // Pour multi-doc : pas de cible unique, on laisse le user cliquer sur le SujetDocsButton ci-dessus
                        if (!sousSujet.documents || sousSujet.documents.length === 0) setShowDocViewer(true);
                    }}>
                        <FileText size={14} />
                        <span>
                            Pré-requis : <strong>avoir pris connaissance{' '}
                            {sousSujet.documents && sousSujet.documents.length > 1
                                ? `des ${sousSujet.documents.length} documents sources`
                                : 'du document'}</strong> avant de répondre aux questions.
                        </span>
                    </div>
                )}

                {onLaunch && nbQ > 0 && !sousSujet.has_session && accessible && (
                    <div className="qcm-popup-launch">
                        {nbQ > 10 && (
                            <div className="mode-picker-section">
                                <span className="mode-picker-label">Nombre de questions</span>
                                <div className="mode-picker-options-row">
                                    {NB_OPTIONS.filter(n => n <= nbQ).map(n => (
                                        <button key={n} className={`sl-examen-option ${pickerNb === n ? 'active' : ''}`} onClick={() => setPickerNb(n)}>{n}</button>
                                    ))}
                                    {!NB_OPTIONS.includes(nbQ) && (
                                        <button className={`sl-examen-option ${pickerNb === nbQ ? 'active' : ''}`} onClick={() => setPickerNb(nbQ)}>
                                            Toutes ({nbQ})
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="mode-picker-section">
                            <span className="mode-picker-label">Mode</span>
                            <div className="mode-picker-options mode-picker-options--row">
                                <button className={`mode-option mode-option-apprentissage ${pickerMode === 'apprentissage' ? 'selected' : ''}`} onClick={() => setPickerMode('apprentissage')}>
                                    <span className="mode-option-icon"><BookOpen size={20} strokeWidth={1.8} /></span>
                                    <strong>Apprentissage</strong>
                                    <span className="mode-option-desc">Correction immédiate</span>
                                </button>
                                <button className={`mode-option mode-option-quiz ${pickerMode === 'quiz' ? 'selected' : ''}`} onClick={() => setPickerMode('quiz')}>
                                    <span className="mode-option-icon"><Clock size={20} strokeWidth={1.8} /></span>
                                    <strong>Classique</strong>
                                    <span className="mode-option-desc">Résultat à la fin</span>
                                </button>
                            </div>
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} onClick={() => { onLaunch(pickerMode, nbQ > 10 ? pickerNb : null); onClose(); }}>
                            Lancer le quiz ({nbQ > 10 ? pickerNb : nbQ} questions{(d => d ? ` · ${d}` : '')(estimateSessionDuration(sousSujet.sec_per_q, pickerMode, nbQ > 10 ? pickerNb : nbQ))})
                        </button>
                    </div>
                )}

                {onLaunch && sousSujet.has_session && accessible && (
                    <div className="qcm-popup-launch">
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { onLaunch(sousSujet.session_mode, null); onClose(); }}>
                            Reprendre la session
                        </button>
                    </div>
                )}

                {onLaunch && nbQ > 0 && !accessible && (
                    <div className="qcm-popup-launch">
                        {/* Le verrou ouvre directement les offres : c'est le moment où l'on veut débloquer. */}
                        <button className="btn btn-primary" style={{ width: '100%' }} disabled={!onUnlock}
                            onClick={() => { onClose(); onUnlock?.(); }}>
                            <Lock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                            Débloquer avec l’accès complet
                        </button>
                        <p style={{ marginTop: 8, fontSize: '0.85em', color: 'var(--text-muted)', textAlign: 'center' }}>
                            Ce sujet est inclus dans l’accès complet à la thématique.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function QcmCard({ sousSujet, scope, hasAccess, user, onStart, onLocked, onDirectLaunch, isIgnored = false, isLastPlayed = false }) {
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);
    const [showDoc, setShowDoc] = useState(false);
    const [reviewStats, setReviewStats] = useState(null);
    const isReviewer = user?.role === 'admin' || user?.role === 'reviewer';
    const isAdmin = user?.role === 'admin';
    // Compteur de relecture = questions éditoriales seulement : ni perso, ni communautaire
    // (partner_questions ne passent pas par ce circuit, le compteur y resterait à 0).
    const showReviewStats = isReviewer && !sousSujet.custom_ss_id && scope?.content_source !== 'partner';

    useEffect(() => {
        if (!showReviewStats || !sousSujet.id) return;
        api.post('/admin/questions.php', { action: 'review_stats', sous_sujet_id: sousSujet.id })
            .then(({ data }) => setReviewStats(data.data))
            .catch(e => console.warn("Erreur:", e));
    }, [sousSujet.id, showReviewStats]);

    const accessible = hasAccess || sousSujet.is_demo;
    const locked = user && !accessible;
    const isEdition = sousSujet.status === 'EDITION';
    const nbQ = sousSujet.nb_questions || 0;
    const answered = sousSujet.answered_count || 0;
    const mastered = sousSujet.mastered_count || 0;
    const answeredPct = nbQ > 0 ? Math.min(100, Math.round((answered / nbQ) * 100)) : 0;
    const masteredPct = nbQ > 0 ? Math.min(100, Math.round((mastered / nbQ) * 100)) : 0;

    // Description tronquée à 3 lignes par défaut, expand sur clic "Lire la suite".
    // Seuil 180 caractères pour éviter d'afficher le toggle sur les courtes descriptions.
    const [descExpanded, setDescExpanded] = useState(false);
    const descTruncatable = !!(sousSujet.description && sousSujet.description.length > 180);

    const handleCardClick = () => {
        if (user && nbQ > 0) setShowPopup(true);
    };

    const isObsolete = sousSujet.custom_status === 'OBSOLETE';

    return (
        <div
            className={`qcm-card ${locked ? 'qcm-card-locked' : ''} ${isEdition ? 'qcm-card-edition' : ''} ${user && nbQ > 0 && !isObsolete ? 'qcm-card-clickable' : ''} ${isIgnored ? 'qcm-card-ignored' : ''} ${isLastPlayed ? 'qcm-card-last-played' : ''}`}
            data-ss-id={sousSujet.id}
            onClick={isObsolete ? undefined : handleCardClick}
        >
            <div className="qcm-card-header">
                <div className="qcm-card-title-row">
                    <h4 className="qcm-card-title">{sousSujet.label}</h4>
                    {isEdition && <span className="qcm-badge-edition">Brouillon</span>}
                    {/* Mise en avant posée par l'admin pour une durée limitée (migration 167). */}
                    {sousSujet.nouveau && <span className="qcm-badge-new">Nouveau</span>}
                    {/* Sujet marque demo : "Gratuit" pour qui n'a pas l'acces (c'est
                        l'offre), simple flag "Demo" pour l'admin qui a acces a tout
                        et a besoin de reperer l'etat du drapeau d'un coup d'oeil. */}
                    {!!Number(sousSujet.is_demo) && (
                        !hasAccess
                            ? <span className="qcm-badge-demo">Gratuit</span>
                            : isAdmin && (
                                <span className="qcm-badge-demo qcm-badge-demo--flag" title="Sujet marque demo : accessible sans abonnement">
                                    Démo
                                </span>
                            )
                    )}
                    {(sousSujet.qrc_count || 0) > 0 && (
                        !hasAccess && sousSujet.qrc_has_demo
                            ? <span className="qcm-badge-qrc qcm-badge-qrc--gift" title="Des exemples de réponses rédigées corrigées sont visibles gratuitement"><PenLine size={10} /> Exemple corrigé offert</span>
                            : <span className="qcm-badge-qrc" title="Ce sujet propose des questions à réponse rédigée, corrigées par IA"><PenLine size={10} /> Réponse rédigée</span>
                    )}
                    {locked && <span className="qcm-badge-locked">Verrouillé</span>}
                </div>
                {sousSujet.description && (
                    <>
                        <p className={`qcm-card-desc${descTruncatable && !descExpanded ? ' qcm-card-desc--clamped' : ''}`}>
                            {sousSujet.description}
                        </p>
                        {descTruncatable && (
                            <button
                                type="button"
                                className="qcm-card-desc-toggle"
                                onClick={(e) => { e.stopPropagation(); setDescExpanded(v => !v); }}
                            >
                                {descExpanded ? 'Réduire' : 'Lire la suite'}
                            </button>
                        )}
                    </>
                )}
            </div>
            {sousSujet.documents && sousSujet.documents.length > 0 ? (
                <div className="qcm-card-doc-fab" onClick={e => e.stopPropagation()} style={{ padding: 0, background: 'none', border: 'none' }}>
                    <SujetDocsButton documents={sousSujet.documents} compact />
                </div>
            ) : sousSujet.doc_path && (
                <button className="qcm-card-doc-fab" onClick={e => { e.stopPropagation(); setShowDoc(true); }}
                    title={sousSujet.doc_name || 'Voir le document source'}>
                    <FileText size={14} />
                </button>
            )}

            {showPopup && createPortal(
                <CardDetailPopup
                    sousSujet={sousSujet}
                    scope={scope}
                    onClose={() => setShowPopup(false)}
                    onLaunch={onDirectLaunch ? (mode, nb) => onDirectLaunch(sousSujet.id, mode, nb) : null}
                    onUnlock={user ? onLocked : null}
                    accessible={accessible}
                />,
                document.body
            )}
            {showDoc && sousSujet.doc_path && createPortal(
                <DocumentViewer src={sousSujet.doc_path} name={sousSujet.doc_name} onClose={() => setShowDoc(false)} />,
                document.body
            )}

            <div className="qcm-card-meta">
                <span className="qcm-card-questions">
                    {nbQ} question{nbQ > 1 ? 's' : ''} disponible{nbQ > 1 ? 's' : ''}
                </span>
                {(sousSujet.qrc_count || 0) > 0 && (
                    <span className="qcm-card-qrc" title="Questions à réponse rédigée, corrigées par IA">
                        <PenLine size={11} /> {sousSujet.qrc_count} rédigée{sousSujet.qrc_count > 1 ? 's' : ''}
                    </span>
                )}
                {showReviewStats && reviewStats && (
                    <span className="qcm-review-stats">
                        <span className="qcm-rs-ok" title={`${reviewStats.validated} validée(s)`}>
                            <CheckCircle size={11} />{reviewStats.validated}
                        </span>
                        {reviewStats.to_check > 0 && (
                            <span className="qcm-rs-warn" title={`${reviewStats.to_check} à vérifier`}>
                                <AlertTriangle size={11} />{reviewStats.to_check}
                            </span>
                        )}
                    </span>
                )}
            </div>

            {/* Progression : 2 barres distinctes (Vues bleu / Maîtrisées vert), label en face */}
            {user && nbQ > 0 && (answered > 0 || mastered > 0) && (
                <div className="qcm-card-progression-dual">
                    <div className="qcm-prog-row" title={`${answered} vues sur ${nbQ}`}>
                        <Eye size={12} className="qcm-prog-icon qcm-prog-icon-seen" />
                        <span className="qcm-prog-label">Vues</span>
                        <div className="qcm-progression-bar">
                            <div
                                className="qcm-progression-fill qcm-progression-fill-seen"
                                style={{ width: `${answeredPct}%` }}
                            />
                        </div>
                        <span className="qcm-progression-label">{answered}/{nbQ}</span>
                    </div>
                    <div className="qcm-prog-row" title={`${mastered} maîtrisées sur ${nbQ}`}>
                        <CheckCircle size={12} className="qcm-prog-icon qcm-prog-icon-ok" />
                        <span className="qcm-prog-label">Maîtrisées</span>
                        <div className="qcm-progression-bar">
                            <div
                                className="qcm-progression-fill qcm-progression-fill-ok"
                                style={{ width: `${masteredPct}%` }}
                            />
                        </div>
                        <span className="qcm-progression-label">{mastered}/{nbQ}</span>
                    </div>
                </div>
            )}

            {/* Stats si disponibles — les tentatives sont affichées dans la ligne Session quand une session est en cours */}
            {sousSujet.stats && (sousSujet.stats.best_score != null || (sousSujet.stats.attempts > 0 && !sousSujet.has_session)) && (
                <div className="qcm-card-stats">
                    {sousSujet.stats.best_score != null && (
                        <div className="qcm-stat">
                            <div className="qcm-stat-bar">
                                <div
                                    className="qcm-stat-fill"
                                    style={{
                                        width: `${sousSujet.stats.best_pct}%`,
                                        background: sousSujet.stats.best_pct >= 75 ? '#28a745' : sousSujet.stats.best_pct >= 50 ? '#e67e22' : '#dc3545',
                                    }}
                                />
                            </div>
                            <span className="qcm-stat-label">Meilleur : {sousSujet.stats.best_pct}%</span>
                        </div>
                    )}
                    {sousSujet.stats.attempts > 0 && !sousSujet.has_session && (
                        <span className="qcm-stat-attempts">{sousSujet.stats.attempts} tentative{sousSujet.stats.attempts > 1 ? 's' : ''}</span>
                    )}
                </div>
            )}

            {/* Session en cours — mode + tentatives sur une seule ligne */}
            {!!Number(sousSujet.has_session) && (
                <div className="qcm-card-session">
                    <span className="qcm-session-dot" />
                    <span>
                        Session <strong>{sousSujet.session_mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}</strong> en cours
                        {sousSujet.stats?.attempts > 0 && (
                            <> · {sousSujet.stats.attempts} tentative{sousSujet.stats.attempts > 1 ? 's' : ''}</>
                        )}
                    </span>
                </div>
            )}

            <div className="qcm-card-action" onClick={e => e.stopPropagation()}>
                {sousSujet.custom_status === 'OBSOLETE' ? (
                    <button className="btn btn-outline btn-sm qcm-card-btn" disabled>
                        Sujet obsolète
                    </button>
                ) : !isIgnored ? (
                    <>
                        {nbQ === 0 ? (
                            <button className="btn btn-outline btn-sm qcm-card-btn" disabled>
                                Bientôt disponible
                            </button>
                        ) : accessible && user && sousSujet.has_session ? (() => {
                            // Compteur de la SESSION en cours (peut différer du total du sujet)
                            const sessAnswered = sousSujet.session_answered ?? 0;
                            const sessTotal    = sousSujet.session_total ?? 0;
                            return (
                                <button onClick={() => onStart?.(sousSujet.id, sousSujet.session_mode)} className="btn btn-primary btn-sm qcm-card-btn btn--with-count">
                                    Reprendre
                                    {sessTotal > 0 && <span className="btn-count-badge">{sessAnswered}/{sessTotal}</span>}
                                </button>
                            );
                        })() : accessible && user ? (
                            <button onClick={() => setShowPopup(true)} className="btn btn-primary btn-sm qcm-card-btn">
                                Commencer
                            </button>
                        ) : !user ? (
                            <button onClick={() => navigate('/connexion')} className="btn btn-primary btn-sm qcm-card-btn">
                                Se connecter
                            </button>
                        ) : (
                            <button onClick={() => onLocked?.()} className="btn btn-outline btn-sm qcm-card-btn">
                                Abonnement requis
                            </button>
                        )}
                    </>
                ) : (
                    <span className="qcm-card-ignored-label">Non concerné</span>
                )}
            </div>
        </div>
    );
}
